<template lang="pug">
    section
        el-row(:gutter="40")
            el-col(:lg="8" :md="8")
                el-form-item(:label="$t('Name')" prop="name")
                    el-input(type="text" v-model="name" maxlength="255" :show-word-limit="true")
                el-form-item(:label="$t('Description')" prop="description")
                    el-input(type="textarea" v-model="description" :rows="4" maxlength="1000" :show-word-limit="true")
                    .help {{ $t('Internal note, not shown to attendees.') }}
                el-alert.usage(
                    v-if="eventsCount"
                    type="info"
                    :closable="false"
                    show-icon
                    :title="$tc('Used by {count} event(s)', eventsCount, { count: eventsCount })"
                )

            el-col(:lg="16" :md="16")
                el-form-item(:label="$t('Form fields')")
                    .help {{ $t('First name, last name, email and slot are part of every form. Salutation and phone are included by default but can be removed.') }}
                    //- Laid out like the form itself: the widths decide how the cards share a row.
                    //- Cards are dragged by their grip; the arrows stay for keyboard-friendly steps.
                    draggable.field-list(
                        :value="fields"
                        handle=".field-card-grip"
                        :animation="150"
                        ghost-class="is-ghost"
                        @input="reorderFields"
                    )
                        field-card(
                            v-for="(field, index) in fields"
                            :key="field.id"
                            :class="`is-${field.width || 'full'}`"
                            :field="field"
                            :index="index"
                            :total="fields.length"
                        )

                    .add-field
                        span.add-field-label {{ $t('Add field') }}
                        .add-field-buttons
                            //- optional fixed fields that were removed can come back
                            el-button(
                                v-for="fixed in missingOptionalFixedFields"
                                :key="fixed.key"
                                size="mini"
                                plain
                                :icon="`el-icon-fa-${fixed.icon} fas`"
                                @click="addFixedField(fixed.key)"
                            ) {{ $t(`fixed_field.${fixed.key}`) }}
                            el-button(
                                v-for="fieldType in fieldTypes"
                                :key="fieldType.type"
                                size="mini"
                                plain
                                type="primary"
                                :icon="`el-icon-fa-${fieldType.icon} fas`"
                                @click="addField(fieldType.type)"
                            ) {{ $t(`field_type.${fieldType.type}`) }}
</template>

<i18n>
{
  "en": {
    "Used by {count} event(s)": "Not used by any event yet | Used by 1 event | Used by {count} events",
    "fixed_field.salutation": "Salutation",
    "fixed_field.phone": "Phone",
    "field_type.text": "Text",
    "field_type.textarea": "Text area",
    "field_type.number": "Number",
    "field_type.date": "Date",
    "field_type.time": "Time",
    "field_type.select": "Dropdown",
    "field_type.radio": "Radio buttons",
    "field_type.checkbox": "Checkbox",
    "field_type.checkbox_group": "Checkboxes"
  },
  "de": {
    "Name": "Name",
    "Description": "Beschreibung",
    "Internal note, not shown to attendees.": "Interne Notiz, wird Teilnehmern nicht angezeigt.",
    "Used by {count} event(s)": "Noch von keinem Event verwendet | Wird von 1 Event verwendet | Wird von {count} Events verwendet",
    "Form fields": "Formularfelder",
    "First name, last name, email and slot are part of every form. Salutation and phone are included by default but can be removed.": "Vorname, Nachname, E-Mail und Termin sind in jedem Formular enthalten. Anrede und Telefon sind standardmäßig dabei, können aber entfernt werden.",
    "fixed_field.salutation": "Anrede",
    "fixed_field.phone": "Telefon",
    "Add field": "Feld hinzufügen",
    "field_type.text": "Textfeld",
    "field_type.textarea": "Textbereich",
    "field_type.number": "Zahl",
    "field_type.date": "Datum",
    "field_type.time": "Uhrzeit",
    "field_type.select": "Dropdown",
    "field_type.radio": "Optionsfelder",
    "field_type.checkbox": "Checkbox",
    "field_type.checkbox_group": "Checkboxen"
  }
}
</i18n>

<script>

import _ from 'lodash'
import { useEventManagerRegistrationTemplateStore } from '@/stores'
import { FIELD_TYPES, FIXED_FIELDS, FIXED_OPTIONAL } from '@/stores/eventManagerRegistrationTemplate'

import draggable from 'vuedraggable'
import FieldCard from './Partials/FieldCard'

export default {
    name: 'Main',
    components: {
        draggable,
        FieldCard,
    },
    data() {
        return {
            fieldTypes: FIELD_TYPES,
        }
    },
    computed: {
        registrationTemplateStore() {
            return useEventManagerRegistrationTemplateStore();
        },
        form() {
            return this.registrationTemplateStore.form;
        },
        fields() {
            return _.get(this.form, 'fields') || []
        },
        eventsCount() {
            return _.get(this.form, 'events_count') || 0
        },
        missingOptionalFixedFields() {
            return FIXED_FIELDS.filter(fixed =>
                FIXED_OPTIONAL.includes(fixed.key) && !_.find(this.fields, { key: fixed.key })
            )
        },
        name: {
            get() {
                return _.get(this.form, 'name', '')
            },
            set(value) {
                this.registrationTemplateStore.setFormName(value);
            }
        },
        description: {
            get() {
                return _.get(this.form, 'description') || ''
            },
            set(value) {
                this.registrationTemplateStore.setFormDescription(value);
            }
        },
    },
    methods: {
        addField(type) {
            this.registrationTemplateStore.addField(type)
        },
        addFixedField(key) {
            this.registrationTemplateStore.addFixedField(key)
        },
        reorderFields(fields) {
            this.registrationTemplateStore.reorderFields(fields)
        },
    },
}
</script>

<style lang="sass" scoped>
    .help
        line-height: 1.4
    .usage
        margin-top: 8px

    .field-list
        display: grid
        grid-template-columns: repeat(6, minmax(0, 1fr))
        align-items: start
        gap: 10px
        margin-top: 8px
        ::v-deep .field-card
            min-width: 0
            grid-column: span 6
            &.is-ghost
                opacity: 0.4
                border-style: dashed
                border-color: $--color-primary
            &.is-half
                grid-column: span 3
            &.is-third
                grid-column: span 2

    .add-field
        margin-top: 14px
        .add-field-label
            display: block
            font-size: 12px
            color: $color-grey-text
            margin-bottom: 6px
            line-height: 1.4
        .add-field-buttons
            display: flex
            flex-wrap: wrap
            gap: 6px
            .el-button + .el-button
                margin-left: 0
</style>
